import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Loader2, Download, Search, PencilLine, Trash2, Plus, Building2 } from 'lucide-react';
import FranchiseChangeDialog from '@/components/FranchiseChangeDialog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } });

export default function InvoiceEditHistory() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filters, setFilters] = React.useState({ invoice_number: '', edited_by: '', start_date: '', end_date: '' });
  const [canEdit, setCanEdit] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [invNo, setInvNo] = React.useState('');
  const [invoice, setInvoice] = React.useState(null);
  const [items, setItems] = React.useState([]);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [showFranchise, setShowFranchise] = React.useState(false);
  const isAdmin = (() => { try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '') === 'admin'; } catch { return false; } })();

  const load = React.useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/invoice-edit-history?' + p, auth())
      .then((r) => setRows(r.data.history))
      .catch((e) => toast.error(e.response?.data?.detail || 'Load failed'))
      .finally(() => setLoading(false));
  }, [filters]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => {
    axios.get(API + '/accounts/invoice-edit-permission', auth())
      .then((r) => setCanEdit(r.data.can_edit_finalized)).catch(() => {});
  }, []);

  const lookup = () => {
    axios.get(API + '/accounts/invoice-lookup/' + invNo.trim(), auth())
      .then((r) => {
        if (!['finalized', 'partially_paid', 'paid'].includes(r.data.invoice.status)) {
          return toast.error('Only finalized invoices can be edited here (status: ' + r.data.invoice.status + ')');
        }
        setInvoice(r.data.invoice);
        setItems(r.data.invoice.items.map((it) => ({ ...it })));
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Invoice not found'));
  };

  const setItem = (i, field, val) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: val };
    setItems(next);
  };

  const save = () => {
    if (!reason.trim()) return toast.error('Reason for edit is mandatory');
    setBusy(true);
    axios.put(API + `/accounts/invoices/${invoice.id}/finalized-edit`, {
      reason, items: items.map((it) => ({ ...it, quantity: Number(it.quantity), unit_price: Number(it.unit_price), mrp: Number(it.mrp || 0), discount_percent: Number(it.discount_percent || 0), gst_percentage: Number(it.gst_percentage || 0) })),
    }, auth())
      .then((r) => { toast.success(`Saved — version ${r.data.version}, new total ₹${r.data.total_amount}`); setShowEdit(false); setInvoice(null); setInvNo(''); setReason(''); load(); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Save failed'))
      .finally(() => setBusy(false));
  };

  const exportXlsx = () => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    axios.get(API + '/accounts/invoice-edit-history/export.xlsx?' + p, { ...auth(), responseType: 'blob' })
      .then((r) => {
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = Object.assign(document.createElement('a'), { href: url, download: 'invoice-edit-history.xlsx' });
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      }).catch(() => toast.error('Export failed'));
  };

  return (
    <div className="p-6 space-y-4" data-testid="invoice-edit-history-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Invoice Edit History</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportXlsx} data-testid="ieh-export-btn"><Download className="w-4 h-4 mr-1" />Export</Button>
          <CsvDownloadButton onDownload={exportXlsx} testId="ieh-export-csv" size="default" />
          {isAdmin && <Button variant="outline" onClick={() => setShowFranchise(true)} data-testid="ieh-change-franchise-btn"><Building2 className="w-4 h-4 mr-1" />Change Franchise</Button>}
          {canEdit && <Button onClick={() => setShowEdit(true)} data-testid="ieh-edit-invoice-btn"><PencilLine className="w-4 h-4 mr-1" />Edit Finalized Invoice</Button>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input placeholder="Invoice No" className="h-9 w-40" value={filters.invoice_number} onChange={(e) => setFilters({ ...filters, invoice_number: e.target.value })} data-testid="ieh-invoice-filter" />
        <Input placeholder="Edited By" className="h-9 w-40" value={filters.edited_by} onChange={(e) => setFilters({ ...filters, edited_by: e.target.value })} data-testid="ieh-editedby-filter" />
        <Input type="date" className="h-9 w-36" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} data-testid="ieh-start-date" />
        <Input type="date" className="h-9 w-36" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} data-testid="ieh-end-date" />
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="ieh-table">
            <thead><tr className="border-b bg-slate-50 text-left">
              <th className="p-2">Invoice</th><th className="p-2">Version</th><th className="p-2">Edited By</th>
              <th className="p-2">Date & Time</th><th className="p-2">Reason</th>
              <th className="p-2 text-right">Prev Total</th><th className="p-2 text-right">New Total</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">No invoice edits recorded</td></tr> : rows.map((h) => (
                <tr key={h.id} className="border-b hover:bg-slate-50" data-testid={'ieh-row-' + h.invoice_number + '-v' + h.version}>
                  <td className="p-2 font-mono">{h.invoice_number}</td>
                  <td className="p-2">v{h.version}</td>
                  <td className="p-2">{h.edited_by_name}</td>
                  <td className="p-2 text-xs">{(h.edited_at || '').slice(0, 19).replace('T', ' ')}</td>
                  <td className="p-2 text-xs max-w-[240px]">{h.reason}</td>
                  <td className="p-2 text-right">{h.previous?.total_amount}</td>
                  <td className="p-2 text-right font-semibold">{h.new?.total_amount}</td>
                </tr>))}
            </tbody>
          </table>
        </div>)}

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="ieh-edit-dialog">
          <DialogHeader><DialogTitle>Edit Finalized Invoice</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Input placeholder="Invoice Number" value={invNo} onChange={(e) => setInvNo(e.target.value)} data-testid="ieh-lookup-input" />
            <Button onClick={lookup} data-testid="ieh-lookup-btn"><Search className="w-4 h-4 mr-1" />Load</Button>
          </div>
          {invoice && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{invoice.customer_name} · {invoice.invoice_number} · v{invoice.edit_version || 0} · Current total ₹{invoice.total_amount}</p>
              <table className="w-full text-xs border rounded">
                <thead><tr className="bg-slate-50 text-left"><th className="p-2">Product</th><th className="p-2">Qty</th><th className="p-2">Price (incl GST)</th><th className="p-2">MRP</th><th className="p-2">Disc %</th><th className="p-2">GST %</th><th className="p-2" /></tr></thead>
                <tbody>{items.map((it, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2">{it.product_name}</td>
                    <td className="p-2"><Input type="number" className="h-8 w-20" value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} data-testid={'ieh-qty-' + i} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-24" value={it.unit_price} onChange={(e) => setItem(i, 'unit_price', e.target.value)} data-testid={'ieh-price-' + i} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-24" value={it.mrp || ''} onChange={(e) => setItem(i, 'mrp', e.target.value)} data-testid={'ieh-mrp-' + i} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-16" value={it.discount_percent || 0} onChange={(e) => setItem(i, 'discount_percent', e.target.value)} data-testid={'ieh-disc-' + i} /></td>
                    <td className="p-2"><Input type="number" className="h-8 w-16" value={it.gst_percentage || 0} onChange={(e) => setItem(i, 'gst_percentage', e.target.value)} data-testid={'ieh-gst-' + i} /></td>
                    <td className="p-2"><Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={'ieh-remove-' + i}><Trash2 className="w-4 h-4" /></Button></td>
                  </tr>))}</tbody>
              </table>
              <Textarea placeholder="Reason for edit (mandatory)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="ieh-reason-input" />
              <Button onClick={save} disabled={busy} data-testid="ieh-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
            </div>)}
        </DialogContent>
      </Dialog>
      <FranchiseChangeDialog entity="invoice" open={showFranchise} onOpenChange={setShowFranchise} />
    </div>
  );
}
